import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { filterCommanderOptions } from "./commanderUtils";

const useCommanderSearch = ({
  mode = "local",
  query = "",
  localOptions = [],
  fetchRemote,
  enabled = true,
  debounceMs = 250,
}) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestSeqRef = useRef(0);

  const queryText = String(query || "");

  const localFiltered = useMemo(() => {
    if (mode !== "local") return [];
    return filterCommanderOptions(localOptions, queryText);
  }, [mode, localOptions, queryText]);

  const runRemote = useCallback(async () => {
    if (!enabled || mode !== "remote" || typeof fetchRemote !== "function") {
      setOptions([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;

    setIsLoading(true);
    setError(null);

    try {
      const next = await fetchRemote({ query: queryText });
      if (requestSeqRef.current !== seq) return;
      setOptions(Array.isArray(next) ? next : []);
    } catch (err) {
      if (requestSeqRef.current !== seq) return;
      setOptions([]);
      setError(err);
    } finally {
      if (requestSeqRef.current === seq) {
        setIsLoading(false);
      }
    }
  }, [enabled, mode, fetchRemote, queryText]);

  useEffect(() => {
    if (!enabled) {
      setOptions([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (mode === "local") {
      setOptions(localFiltered);
      setIsLoading(false);
      setError(null);
      return;
    }

    const timeout = setTimeout(() => {
      runRemote();
    }, Math.max(0, Number(debounceMs || 0)));

    return () => clearTimeout(timeout);
  }, [enabled, mode, localFiltered, runRemote, debounceMs]);

  return {
    options: mode === "local" ? localFiltered : options,
    isLoading,
    error,
    refresh: runRemote,
  };
};

export default useCommanderSearch;
